import { PRETTY } from '@atlassian/jira-common-constants';
import { statusCategoryForId } from '@atlassian/jira-common-constants/src/status-categories';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import type { IntlShape } from '@atlassian/jira-intl';
import {
	COMPONENTS_GQL_FIELD,
	DATE_PICKER_GQL_FIELD,
	DATE_TIME_GQL_FIELD,
	EPIC_LINK_GQL_FIELD,
	LABEL_GQL_FIELD,
	MULTI_SELECT_GQL_FIELD,
	MULTI_USER_PICKER_GQL_FIELD,
	NUMBER_GQL_FIELD,
	ORIGINAL_ESTIMATE_GQL_FIELD,
	PEOPLE_GQL_FIELD,
	TEXT_GQL_FIELD,
	SINGLE_SELECT_GQL_FIELD,
	USER_PICKER_GQL_FIELD,
	TIME_TRACKING_GQL_FIELD,
	CATEGORY_TYPE,
	STATUS_GQL_FIELD,
	ISSUE_TYPE_GQL_FIELD,
	PRIORITY_GQL_FIELD,
	ISSUE_LINKS_TYPE,
} from '@atlassian/jira-platform-field-config';
import {
	type TimeFormat,
	type TimeUnit,
	timeTrackingFormatter,
} from '@atlassian/jira-time-tracking-formatter';
import {
	BOARD_FIELD_TEXT,
	BOARD_FIELD_COMPONENTS,
	BOARD_FIELD_LABEL,
	BOARD_FIELD_TIME_ORIGINAL_ESTIMATE,
	BOARD_FIELD_DATE,
	BOARD_FIELD_SELECT,
	BOARD_FIELD_DATE_TIME,
	BOARD_FIELD_FLOAT,
	BOARD_FIELD_USER,
	BOARD_FIELD_PARENT,
	BOARD_FIELD_STATUS,
	BOARD_FIELD_ISSUE_TYPE,
	BOARD_FIELD_PRIORITY,
	BOARD_FIELD_CATEGORY,
	BOARD_FIELD_CONFLUENCE_PAGES_COUNT,
	BOARD_FIELD_ISSUE_LINKS_COUNT,
} from '../../common/constants';
import type {
	BoardIssueConfluenceLinksCountFieldOld,
	BoardIssueFieldOld,
	ExtendedFields,
} from '../../common/types';
import type { IssueDetailFieldJIS2, IssueDetailsJIS2 } from '../../services/board-issues-old';

const emptyFieldsArray: never[] = [];

const transformers: Record<
	string,
	(field: IssueDetailFieldJIS2, intl?: IntlShape) => BoardIssueFieldOld | null
> = {
	[TEXT_GQL_FIELD]: (field) => {
		if (field.__typename !== TEXT_GQL_FIELD || field.text == null) {
			return null;
		}
		return {
			type: BOARD_FIELD_TEXT,
			fieldId: field.fieldId,
			name: field.name,
			value: field.text,
		};
	},
	[COMPONENTS_GQL_FIELD]: (field) => {
		if (field.__typename !== COMPONENTS_GQL_FIELD) {
			return null;
		}
		const value = field.selectedComponentsConnection?.edges
			?.map((edge) => edge?.node?.name ?? null)
			.filter((name): name is string => name != null);

		if (value == null || value.length === 0) {
			return null;
		}

		return {
			type: BOARD_FIELD_COMPONENTS,
			fieldId: field.fieldId,
			name: field.name,
			value,
		};
	},
	[LABEL_GQL_FIELD]: (field) => {
		if (field.__typename !== LABEL_GQL_FIELD || field.selectedLabelsConnection == null) {
			return null;
		}
		const value = field.selectedLabelsConnection.edges
			?.map((edge) => edge?.node?.name ?? null)
			.filter((name): name is string => name != null);
		if (value == null || value.length === 0) {
			return null;
		}
		return {
			type: BOARD_FIELD_LABEL,
			fieldId: field.fieldId,
			value,
		};
	},
	[ORIGINAL_ESTIMATE_GQL_FIELD]: (field) => {
		if (
			field.__typename !== ORIGINAL_ESTIMATE_GQL_FIELD ||
			field.originalEstimate?.timeInSeconds == null
		) {
			return null;
		}
		// TODO: FE needs estimateString from JIS2 JET-4805
		return {
			type: BOARD_FIELD_TIME_ORIGINAL_ESTIMATE,
			fieldId: field.fieldId,
			name: field.name,
			value: field.originalEstimate.timeInSeconds,
		};
	},
	[TIME_TRACKING_GQL_FIELD]: (field, intl?: IntlShape) => {
		if (
			field.__typename !== TIME_TRACKING_GQL_FIELD ||
			field.originalEstimate?.timeInSeconds == null ||
			field.timeTrackingSettings?.workingHoursPerDay == null ||
			field.timeTrackingSettings?.workingDaysPerWeek == null ||
			field.timeTrackingSettings?.defaultUnit == null ||
			intl == null
		) {
			return null;
		}
		const {
			workingHoursPerDay,
			workingDaysPerWeek,
			defaultFormat: timeFormat,
			defaultUnit,
		} = field.timeTrackingSettings;

		return {
			type: BOARD_FIELD_TIME_ORIGINAL_ESTIMATE,
			// TODO After JIS2 migration completion, replace string constant with fieldId JET-4810
			fieldId: 'timeoriginalestimate',
			name: field.name,
			value: timeTrackingFormatter(
				field.originalEstimate.timeInSeconds,
				{
					workingHoursPerDay,
					workingDaysPerWeek,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					timeFormat: (timeFormat as unknown as TimeFormat) || PRETTY,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					defaultUnit: defaultUnit as unknown as TimeUnit,
				},
				intl,
			),
		};
	},
	[DATE_PICKER_GQL_FIELD]: (field) => {
		if (field.__typename !== DATE_PICKER_GQL_FIELD || field.date == null) return null;
		return {
			type: BOARD_FIELD_DATE,
			fieldId: field.fieldId,
			name: field.name,
			value: field.date,
		};
	},
	[SINGLE_SELECT_GQL_FIELD]: (field) => {
		if (
			field.__typename !== SINGLE_SELECT_GQL_FIELD ||
			field.fieldOption?.value == null ||
			field.type === CATEGORY_TYPE
		)
			return null;

		return {
			type: BOARD_FIELD_SELECT,
			fieldId: field.fieldId,
			name: field.name,
			value: [field.fieldOption.value],
		};
	},
	[DATE_TIME_GQL_FIELD]: (field) => {
		if (field.__typename !== DATE_TIME_GQL_FIELD || field.dateTime == null) {
			return null;
		}
		return {
			type: BOARD_FIELD_DATE_TIME,
			fieldId: field.fieldId,
			name: field.name,
			value: field.dateTime,
		};
	},
	[NUMBER_GQL_FIELD]: (field) => {
		if (field.__typename !== NUMBER_GQL_FIELD || field.number == null) return null;
		return {
			type: BOARD_FIELD_FLOAT,
			fieldId: field.fieldId,
			name: field.name,
			value: field.number,
		};
	},
	[USER_PICKER_GQL_FIELD]: (field) => {
		if (field.__typename !== USER_PICKER_GQL_FIELD || field.user == null) {
			return null;
		}
		return {
			type: BOARD_FIELD_USER,
			fieldId: field.fieldId,
			name: field.name,
			users: [
				{
					accountId: field.user.accountId,
					name: field.user.name,
					avatarURL: field.user.picture,
				},
			],
		};
	},
	[MULTI_USER_PICKER_GQL_FIELD]: (field) => {
		if (field.__typename !== MULTI_USER_PICKER_GQL_FIELD) {
			return null;
		}

		const users = field.selectedUsersConnection?.edges
			?.map((edge) => edge?.node)
			.filter(
				(
					user,
				): user is typeof user & {
					accountId: string;
					name: string;
				} => user != null,
			)
			.map((user) => ({
				accountId: user.accountId,
				name: user.name,
				avatarURL: user.picture,
			}));

		if (users == null || users.length < 1) {
			return null;
		}

		return {
			type: BOARD_FIELD_USER,
			fieldId: field.fieldId,
			name: field.name,
			users,
		};
	},
	[MULTI_SELECT_GQL_FIELD]: (field) => {
		if (field.__typename !== MULTI_SELECT_GQL_FIELD) {
			return null;
		}
		const value = field.selectedOptions?.edges
			?.map((edge) => edge?.node)
			.map((node) => node?.value)
			.filter(
				// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
				(optionValue): optionValue is Exclude<typeof optionValue, null | void> =>
					optionValue != null,
			);
		if (value == null || value.length < 1) {
			return null;
		}

		return {
			type: BOARD_FIELD_SELECT,
			fieldId: field.fieldId,
			name: field.name,
			value,
		};
	},
	[PEOPLE_GQL_FIELD]: (field) => {
		if (field.__typename !== PEOPLE_GQL_FIELD) {
			return null;
		}

		const users = field.selectedUsersConnection?.edges
			?.map((edge) => edge?.node)
			// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
			.filter((user): user is Exclude<typeof user, null | void> => user != null)
			.map((user) => ({
				accountId: user.accountId,
				name: user.name,
				avatarURL: user.picture,
			}));

		if (users == null || users.length < 1) {
			return null;
		}

		return {
			type: BOARD_FIELD_USER,
			fieldId: field.fieldId,
			name: field.name,
			users,
		};
	},
	[EPIC_LINK_GQL_FIELD]: (field) => {
		if (
			field.__typename !== EPIC_LINK_GQL_FIELD ||
			field.epic == null ||
			field.epic.key == null ||
			field.epic.issueId == null
		) {
			return null;
		}
		return {
			type: BOARD_FIELD_PARENT,
			fieldId: field.fieldId,
			name: field.name,
			parent: {
				id: field.epic.issueId,
				key: field.epic.key,
				color: field.epic.color,
				text: field.epic.name,
			},
		};
	},
	[STATUS_GQL_FIELD]: (field) => {
		if (
			field.__typename !== STATUS_GQL_FIELD ||
			field.status.name == null ||
			field.status.statusCategory?.statusCategoryId == null ||
			field.status.statusId == null
		) {
			return null;
		}
		return {
			type: BOARD_FIELD_STATUS,
			fieldId: field.fieldId,
			name: field.name,
			status: {
				category: statusCategoryForId(field.status.statusCategory.statusCategoryId),
				id: Number(field.status.statusId),
				name: field.status.name,
			},
		};
	},
	[ISSUE_TYPE_GQL_FIELD]: (field) => {
		if (
			field.__typename !== ISSUE_TYPE_GQL_FIELD ||
			field.issueType?.issueTypeId == null ||
			field.issueType?.name == null
		) {
			return null;
		}

		return {
			type: BOARD_FIELD_ISSUE_TYPE,
			fieldId: field.fieldId,
			name: field.name,
			id: field.issueType.issueTypeId,
			issueType: {
				id: field.issueType.issueTypeId,
				name: field.issueType.name,
				iconUrl: field.issueType.avatar?.medium ?? '',
			},
		};
	},
	[PRIORITY_GQL_FIELD]: (field) => {
		if (
			field.__typename !== PRIORITY_GQL_FIELD ||
			field.priority?.iconUrl == null ||
			field.priority?.name == null ||
			field.priority?.priorityId == null
		) {
			return null;
		}
		return {
			type: BOARD_FIELD_PRIORITY,
			fieldId: field.fieldId,
			name: field.name,
			priority: {
				iconUrl: field.priority.iconUrl,
				id: field.priority.priorityId,
				name: field.priority.name,
			},
		};
	},
};

const transformField = (
	field: IssueDetailFieldJIS2 | null,
	intl: IntlShape,
): BoardIssueFieldOld | null => {
	if (field == null) return null;

	if (
		field.__typename === SINGLE_SELECT_GQL_FIELD &&
		field.type === CATEGORY_TYPE &&
		field.fieldOption?.value != null
	) {
		return {
			fieldId: field.fieldId,
			id: field.fieldOption.optionId,
			name: field.name,
			type: BOARD_FIELD_CATEGORY,
			value: field.fieldOption.value,
			color: field.fieldOption.color?.colorKey ?? null,
		};
	}

	if (field.__typename in transformers) {
		return transformers[field.__typename](field, intl);
	}

	return null;
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const transformFieldsOld = (issueDetails: IssueDetailsJIS2[], intl: IntlShape) => {
	const toReturn: Record<number, ExtendedFields> = {};
	issueDetails.forEach((issueDetail) => {
		const issueFields: BoardIssueFieldOld[] = [];
		let labelField;
		let categoryField;
		let parentField;
		let confluenceLinksCount: BoardIssueConfluenceLinksCountFieldOld | undefined;

		issueDetail.fieldsByIdOrAlias?.forEach((field) => {
			const transformedField = transformField(field, intl);
			if (transformedField != null) {
				issueFields.push(transformedField);
			}
			if (transformedField?.type === BOARD_FIELD_CATEGORY) {
				categoryField = transformedField;
			}
			if (transformedField?.type === BOARD_FIELD_LABEL) {
				labelField = transformedField;
			}
			if (transformedField?.type === BOARD_FIELD_PARENT) {
				parentField = transformedField;
			}
		});
		if (issueDetail.legacyConfluenceLinks?.linkedConfluencePages?.pageCount != null) {
			confluenceLinksCount = {
				type: BOARD_FIELD_CONFLUENCE_PAGES_COUNT,
				fieldId: 'remoteIssueLinks',
				value: issueDetail.legacyConfluenceLinks.linkedConfluencePages.pageCount,
			};
		}

		if (issueFields.length > 0 || confluenceLinksCount != null) {
			toReturn[issueDetail.issueId] = {
				fields: issueFields,
				fieldsByAlias: {
					label: labelField,
					category: categoryField,
					parent: parentField,
					confluenceLinksCount,
				},
			};
		} else {
			toReturn[issueDetail.issueId] = {
				fields: emptyFieldsArray,
				fieldsByAlias: {},
			};
		}

		if (issueDetail.coverMedia !== undefined) {
			toReturn[issueDetail.issueId].coverMedia = issueDetail.coverMedia;
		}

		if (expValEquals('jwm_linked_issues_on_the_board_milestone_1', 'cohort', 'variation')) {
			if (issueDetail.issueLinks?.edges) {
				toReturn[issueDetail.issueId].fieldsByAlias.issueLinksCount = {
					type: BOARD_FIELD_ISSUE_LINKS_COUNT,
					fieldId: ISSUE_LINKS_TYPE,
					value: issueDetail.issueLinks.edges.filter(Boolean).length ?? 0,
				};
			}
		}
	});

	return toReturn;
};
